import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput, View } from 'react-native';
import { AuthLink } from './components/auth-link';
import { AuthScreenLayout } from './components/auth-screen-layout';
import { useOtpVerification } from './hooks/use-otp-verification';

// The digit's fontSize follows the OS text-size setting but its box is computed in
// raw points, so unbounded scaling clips the glyph. Numeric codes are the standard
// exception to free-scaling type.
const OTP_MAX_FONT_SCALE = 1.4;

export function VerifyOTPScreen() {
  const otp = useOtpVerification();
  const { boxWidth, boxHeight, fontSize, gap } = otp.computeBoxSizes();

  return (
    <AuthScreenLayout
      title="Verify Email"
      heading="Verify your email"
      subheading="We've sent a 6-digit code to:"
      footer={
        <View className="flex-row justify-center">
          <AuthLink onPress={otp.navigateToLogin} testID="otp-login-link">
            Back to log in
          </AuthLink>
        </View>
      }
    >
      <View className="gap-6">
        <Text className="text-base font-semibold text-center text-warm-gray-100">
          {otp.userEmail}
        </Text>

        <View onLayout={otp.handleOtpRowLayout}>
          <View className="flex-row justify-center">
            {otp.otp.map((digit, index) => (
              <TextInput
                key={index}
                ref={(ref) => { otp.inputRefs.current[index] = ref; }}
                className={`text-center font-bold rounded-lg border bg-surface text-warm-gray-100 ${
                  otp.error ? 'border-error' : 'border-border-visible'
                }`}
                style={{
                  width: boxWidth,
                  height: boxHeight,
                  fontSize,
                  marginRight: index < 5 ? gap : 0,
                }}
                maxFontSizeMultiplier={OTP_MAX_FONT_SCALE}
                value={digit}
                onChangeText={(value) => otp.handleOTPChange(value, index)}
                onKeyPress={(event) => otp.handleKeyPress(event, index)}
                keyboardType="numeric"
                textContentType={index === 0 ? 'oneTimeCode' : 'none'}
                autoComplete={index === 0 ? 'sms-otp' : 'off'}
                importantForAutofill={index === 0 ? 'yes' : 'no'}
                selectTextOnFocus
                editable={!otp.isLoading}
                accessibilityLabel={`Verification code digit ${index + 1} of 6`}
              />
            ))}
          </View>

          {otp.error && (
            <Text
              className="text-error text-xs text-center mt-2"
              accessibilityLiveRegion="polite"
            >
              {otp.error}
            </Text>
          )}
        </View>

        <Button
          size="lg"
          fullWidth
          onPress={() => otp.handleVerifyOTP()}
          loading={otp.isLoading}
          disabled={otp.otp.some((d) => d === '')}
          testID="otp-submit"
        >
          Verify code
        </Button>

        <View
          className="flex-row justify-center items-center gap-1"
          testID="otp-resend-row"
        >
          <Text className="text-sm text-warm-gray-300">Didn&apos;t receive the code?</Text>
          <AuthLink
            onPress={otp.handleResendOTP}
            disabled={otp.resendCooldown > 0 || otp.isResending}
            testID="otp-resend"
          >
            {otp.resendCooldown > 0
              ? `Resend in ${otp.resendCooldown}s`
              : otp.isResending
                ? 'Sending...'
                : 'Resend code'}
          </AuthLink>
        </View>
      </View>
    </AuthScreenLayout>
  );
}
